import React, { useState, useEffect } from 'react';
import { spareParts } from '../services/api';
import Pagination from '../components/Pagination';
import { compressImage } from '../utils/imageUtils';

const MasterSpareParts = () => {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    last_page: 1,
    per_page: 50,
    current_page: 1,
    from: 0,
    to: 0
  });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    period: '',
    purpose: '',
    is_miscellaneous: 0,
    image: null
  });
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchData(1);
  }, []);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const res = await spareParts.getAll({ page, per_page: 50 });
      setDataList(res.data.data);
      setPagination(res.data.pagination);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch spare parts', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        period: item.period || '',
        purpose: item.purpose || '',
        is_miscellaneous: item.is_miscellaneous ? 1 : 0,
        image: null
      });
    } else {
      setEditingItem(null);
      setFormData({ name: '', description: '', period: '', purpose: '', is_miscellaneous: 0, image: null });
    }
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      if (formData.description) payload.append('description', formData.description);
      payload.append('period', formData.period || '');
      payload.append('purpose', formData.purpose || '');
      payload.append('is_miscellaneous', parseInt(formData.is_miscellaneous));
      
      if (formData.image) {
        const compressedFile = await compressImage(formData.image);
        payload.append('image', compressedFile);
      }
      
      if (editingItem) {
        await spareParts.update(editingItem.id, payload);
      } else {
        await spareParts.create(payload);
      }
      setShowModal(false);
      fetchData(currentPage);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan data.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus spare part ini? Data yang terkait di log services dan mapping mungkin ikut terhapus atau menyebabkan error.')) return;
    try {
      await spareParts.delete(id);
      fetchData(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal menghapus data.');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Master Spare Part</h2>
          <p className="text-gray-500 text-sm mt-1">Kelola daftar spare part global, deskripsi, dan tipe (Utama/Insidental).</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-blue-700 transition shadow-sm"
        >
          + Tambah Spare Part
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nama Spare Part</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Deskripsi</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Periode</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tujuan</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tipe</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Aksi</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500 font-medium">Memuat data...</td>
                </tr>
              ) : dataList.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500 font-medium">Belum ada master spare part terdaftar.</td>
                </tr>
              ) : (
                dataList.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-10 h-10 object-cover rounded shadow-sm border"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center text-xs text-gray-400">
                            No Img
                          </div>
                        )}
                        <span className="text-sm font-bold text-gray-900">{item.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 truncate max-w-xs" title={item.description}>
                        {item.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 truncate max-w-xs" title={item.period}>
                        {item.period || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 truncate max-w-xs" title={item.purpose}>
                        {item.purpose || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.is_miscellaneous ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Insidental / Lain-lain
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                          Part Utama
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button onClick={() => handleOpenModal(item)} className="text-blue-600 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg mr-2 transition">Edit</button>
                      <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900 bg-red-50 px-3 py-1.5 rounded-lg transition">Hapus</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <Pagination 
          pagination={pagination} 
          onPageChange={(page) => fetchData(page)} 
        />
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">{editingItem ? 'Edit Spare Part' : 'Tambah Spare Part Baru'}</h3>
              <p className="text-sm text-gray-500 mt-1">Isi formulir data master spare part.</p>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm font-medium border border-red-100">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nama Spare Part</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Contoh: Oli Mesin"
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Deskripsi</label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Penjelasan fungsi part (Opsional)"
                  rows={3}
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Periode Penggantian (Opsional)</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.period}
                  onChange={e => setFormData({ ...formData, period: e.target.value })}
                  placeholder="Contoh: 10.000 KM atau 6 Bulan"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Tujuan Penggantian (Opsional)</label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                  value={formData.purpose}
                  onChange={e => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="Penjelasan tujuan penggantian part"
                  rows={2}
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Gambar Part (Opsional)</label>
                <div className="flex items-center gap-4">
                  {editingItem?.image && !formData.image && (
                    <img 
                      src={editingItem.image} 
                      className="w-12 h-12 rounded object-cover border shadow-sm"
                      alt="Current"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => setFormData({ ...formData, image: e.target.files[0] })}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Format: JPG, PNG. Maksimal 500KB.</p>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Tipe Part</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="is_misc" 
                      value={0}
                      checked={formData.is_miscellaneous == 0}
                      onChange={e => setFormData({ ...formData, is_miscellaneous: 0 })}
                      className="w-4 h-4 text-blue-600" 
                    />
                    <span className="text-sm text-gray-700">Part Utama</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="is_misc" 
                      value={1}
                      checked={formData.is_miscellaneous == 1}
                      onChange={e => setFormData({ ...formData, is_miscellaneous: 1 })}
                      className="w-4 h-4 text-orange-600" 
                    />
                    <span className="text-sm text-gray-700">Insidental</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2 italic">* Part insidental seperti baut/lain-lain bisa memiliki input nama kustom di app mekanik.</p>
              </div>

              <div className="flex gap-3 mt-8 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`flex-1 py-2.5 text-white font-bold rounded-xl transition shadow-md ${
                    isSaving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
                  }`}
                >
                  {isSaving ? 'Menyimpan...' : 'Simpan Data'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterSpareParts;
